/**
 * Debug logging for the telebirr SuperApp WebView.
 *
 * Everything here writes to console, which vConsole renders on-device — there
 * is no remote inspector inside the SuperApp. Gated by the same switch as
 * vConsole itself, so a normal production build stays silent.
 */

const ENABLED =
  process.env.NODE_ENV !== "production" ||
  process.env.REACT_APP_VCONSOLE === "true";

export const isDebugEnabled = () => ENABLED;

const stamp = () => new Date().toISOString().slice(11, 23);

export const debugLog = (scope, ...args) => {
  if (!ENABLED) return;
  console.log(`[${stamp()}][${scope}]`, ...args);
};

export const debugWarn = (scope, ...args) => {
  if (!ENABLED) return;
  console.warn(`[${stamp()}][${scope}]`, ...args);
};

export const debugError = (scope, ...args) => {
  if (!ENABLED) return;
  console.error(`[${stamp()}][${scope}]`, ...args);
};

/**
 * Tokens are secrets, but "is it empty / truncated / the wrong one" is exactly
 * what we need to see. Show the ends and the length, never the middle.
 */
export const maskToken = (token) => {
  if (token === null || token === undefined || token === "") return `<${String(token)}>`;
  if (typeof token !== "string") return `<non-string: ${typeof token}>`;
  if (token.length <= 12) return `${token.slice(0, 2)}… (len=${token.length})`;
  return `${token.slice(0, 8)}…${token.slice(-4)} (len=${token.length})`;
};

/**
 * Bridge failures arrive as Error objects, bare strings, or plain
 * { code, message } bags depending on the host. Flatten them all.
 */
export const describeError = (error) => {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (typeof error === "object" && error !== null) {
    try {
      return JSON.parse(JSON.stringify(error));
    } catch (_) {
      return { unserializable: String(error) };
    }
  }
  return { value: String(error) };
};

/** Returns a function that logs how long a step took. */
export const debugTimer = (scope, label) => {
  const start = Date.now();
  debugLog(scope, `${label} →`);
  return (outcome, extra) =>
    debugLog(scope, `${label} ← ${outcome} in ${Date.now() - start}ms`, extra ?? "");
};
